import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { 
  CreditCard, Check, ArrowRight, Loader2, Building2, 
  CheckCircle, XCircle, Clock, Receipt, AlertCircle, Ticket, X
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import { usePricing } from '../contexts/PricingContext';

const Billing = () => {
  const { userTheme } = useTheme();
  const { enforced: pricingEnforced } = usePricing();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponPlanId, setCouponPlanId] = useState(null);

  // Get theme values
  const pageStyles = userTheme?.page || {};
  const cardStyles = userTheme?.cards || {};
  const buttonStyles = userTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';

  useEffect(() => {
    fetchData();

    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled');
    }
  }, [searchParams]);

  // Auto-trigger checkout if ?plan= is in URL (from pricing page signup flow)
  useEffect(() => {
    const preselectedPlan = searchParams.get('plan');
    if (preselectedPlan && !loading && plans.length > 0 && !processingPlan) {
      const matchedPlan = plans.find(p => p._id === preselectedPlan || p.id === preselectedPlan);
      if (matchedPlan) {
        toast.info(`Starting checkout for ${matchedPlan.name}...`);
        handleSubscribe(matchedPlan._id || matchedPlan.id);
      }
    }
  }, [loading, plans]);

  const fetchData = async () => {
    try {
      const [plansRes, currentRes, transactionsRes] = await Promise.all([
        api.get('/companies/membership-plans'),
        api.get('/payments/current-plan'),
        api.get('/payments/transactions')
      ]);
      
      setPlans(plansRes.data);
      setCurrentPlan(currentRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPollingStatus('timeout');
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    setPollingStatus('checking');

    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      const { payment_status, status } = res.data;

      if (payment_status === 'paid') {
        setPollingStatus('success');
        toast.success('Payment successful! Your subscription is now active.');
        fetchData(); // Refresh data
        
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname);
        return;
      } else if (status === 'expired') {
        setPollingStatus('expired');
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setPollingStatus('error');
        toast.error('Error checking payment status.');
      }
    }
  };

  // Validate coupon for a specific plan
  const validateCoupon = async (planId) => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setValidatingCoupon(true);
    setCouponPlanId(planId);
    
    try {
      const res = await api.post('/coupons/validate', {
        code: couponCode.trim(),
        plan_id: planId
      });
      
      setAppliedCoupon(res.data);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponPlanId(null);
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    
    try {
      // If coupon is applied and is free_lifetime, handle differently
      if (appliedCoupon && appliedCoupon.coupon_type === 'free_lifetime' && couponPlanId === planId) {
        // Redeem the coupon and activate subscription without Stripe
        const redeemRes = await api.post('/coupons/redeem', {
          code: couponCode.trim(),
          plan_id: planId
        });
        
        if (redeemRes.data.redeemed) {
          // Activate the free subscription
          await api.post('/payments/activate-free', {
            plan_id: planId,
            coupon_code: couponCode.trim()
          });
          
          toast.success('Free lifetime membership activated!');
          removeCoupon();
          fetchData();
          setProcessingPlan(null);
          return;
        }
      }
      
      const originUrl = window.location.origin;
      const payload = {
        plan_id: planId,
        origin_url: originUrl
      };
      
      // Include coupon if applied for this plan
      if (appliedCoupon && couponPlanId === planId) {
        payload.coupon_code = couponCode.trim();
      }
      
      const res = await api.post('/payments/subscribe', payload);
      
      // Redirect to Stripe Checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPlan(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      expired: 'bg-gray-100 text-gray-500',
      failed: 'bg-red-100 text-red-700'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: primaryColor }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Payment Status Banner */}
        {pollingStatus === 'checking' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-xl flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-blue-700">Verifying your payment...</span>
          </div>
        )}
        
        {pollingStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-100 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">Payment successful! Your subscription is now active.</span>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Billing & Subscription</h1>
              <p className="text-gray-500 text-sm">Manage your subscription and view payment history</p>
            </div>
          </div>
        </div>

        {/* Current Plan Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h2>
          
          {currentPlan?.has_plan ? (
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="w-6 h-6 text-blue-600" />
                  <span className="text-xl font-medium text-gray-900">{currentPlan.plan?.name}</span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    {currentPlan.subscription_status}
                  </span>
                </div>
                <p className="text-gray-500">
                  ${currentPlan.plan?.price}/month • {currentPlan.plan?.user_limit || 'Unlimited'} users
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-gray-500">
              <AlertCircle className="w-5 h-5" />
              <span>
                {pricingEnforced
                  ? 'No active subscription. Choose a plan below to get started.'
                  : 'No subscription — the platform is currently free to use.'}
              </span>
            </div>
          )}
        </div>

        {/* Free platform notice — pricing enforcement is off */}
        {!pricingEnforced && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8" data-testid="billing-free-notice">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Trados is currently free to use</p>
                <p className="text-sm text-gray-500 mt-1">
                  Every feature is available on your account at no charge, and there is
                  nothing to buy right now.
                  {currentPlan?.has_plan && ' Your existing subscription remains active and continues to bill as usual.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Coupon Code Input */}
        {pricingEnforced && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-gray-900">Have a coupon code?</span>
          </div>
          
          {appliedCoupon ? (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <code className="font-mono font-bold text-green-700">{appliedCoupon.code}</code>
                </div>
                <p className="text-sm text-green-600 mt-1">{appliedCoupon.message}</p>
              </div>
              <button
                onClick={removeCoupon}
                className="p-1 hover:bg-green-100 rounded transition"
              >
                <X className="w-4 h-4 text-green-600" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono uppercase focus:ring-2 focus:ring-purple-500"
                data-testid="coupon-input"
              />
              <p className="text-xs text-gray-500 self-center">Select a plan to apply</p>
            </div>
          )}
        </div>

        )}

        {/* Available Plans — hidden entirely while the platform is free */}
        {pricingEnforced && (
        <>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Plans</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan?.plan?.id === plan.id;
            const hasCouponForThisPlan = appliedCoupon && couponPlanId === plan.id;
            const showDiscountedPrice = hasCouponForThisPlan && appliedCoupon.discounted_price !== undefined;
            
            return (
              <div 
                key={plan.id}
                className={`bg-white rounded-xl shadow-sm border-2 p-6 relative ${
                  isCurrentPlan ? 'border-blue-500' : 
                  hasCouponForThisPlan ? 'border-green-500' : 'border-gray-100'
                }`}
              >
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                    Current Plan
                  </div>
                )}
                {hasCouponForThisPlan && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-full">
                    Coupon Applied!
                  </div>
                )}
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm mb-4 h-12">{plan.description || 'Standard membership plan'}</p>
                
                <div className="mb-4">
                  {showDiscountedPrice ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-green-600">${appliedCoupon.discounted_price}</span>
                        <span className="text-lg text-gray-400 line-through">${plan.price}</span>
                      </div>
                      <p className="text-sm text-green-600 mt-1">
                        {appliedCoupon.coupon_type === 'free_lifetime' 
                          ? 'Free forever!' 
                          : `Save $${appliedCoupon.savings_per_month?.toFixed(2)}/mo for ${appliedCoupon.duration_months} months`
                        }
                      </p>
                    </div>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-500">
                        {plan.billing_cycle === 'yearly' ? '/year' : plan.billing_cycle === 'one_time' ? ' one-time' : '/monthly'}
                      </span>
                    </>
                  )}
                </div>
                
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    {plan.user_limit ? `Up to ${plan.user_limit} users` : 'Unlimited users'}
                  </li>
                  {plan.show_client_limit !== false && (
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500" />
                      {plan.client_limit ? `Up to ${plan.client_limit} clients` : 'Unlimited clients'}
                    </li>
                  )}
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    {plan.loi_verifications_per_month ? `${plan.loi_verifications_per_month} LOI verifications/month` : 'Unlimited LOI verifications'}
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    Email support
                  </li>
                </ul>
                
                {/* Coupon Apply Button */}
                {!isCurrentPlan && couponCode && !hasCouponForThisPlan && (
                  <button
                    onClick={() => validateCoupon(plan.id)}
                    disabled={validatingCoupon}
                    className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2 border-2 border-purple-500 text-purple-600 rounded-xl font-medium hover:bg-purple-50 transition"
                    data-testid={`apply-coupon-${plan.id}`}
                  >
                    {validatingCoupon && couponPlanId === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Validating...
                      </>
                    ) : (
                      <>
                        <Ticket className="w-4 h-4" />
                        Apply Coupon
                      </>
                    )}
                  </button>
                )}
                
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrentPlan || processingPlan === plan.id}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition ${
                    isCurrentPlan 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : hasCouponForThisPlan
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'text-white hover:opacity-90'
                  }`}
                  style={{ backgroundColor: isCurrentPlan || hasCouponForThisPlan ? undefined : primaryColor }}
                  data-testid={`subscribe-plan-${plan.id}`}
                >
                  {processingPlan === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : hasCouponForThisPlan && appliedCoupon.coupon_type === 'free_lifetime' ? (
                    <>
                      Activate Free
                      <ArrowRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Subscribe
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
          
          {plans.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              No membership plans available yet.
            </div>
          )}
        </div>
        </>
        )}

        {/* Payment History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <button
            onClick={() => setShowTransactions(!showTransactions)}
            className="w-full p-6 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <Receipt className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-900">Payment History</span>
              <span className="text-sm text-gray-500">({transactions.length} transactions)</span>
            </div>
            <ArrowRight className={`w-5 h-5 text-gray-400 transition ${showTransactions ? 'rotate-90' : ''}`} />
          </button>
          
          {showTransactions && transactions.length > 0 && (
            <div className="border-t border-gray-100">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {tx.metadata?.plan_name || 'Subscription Payment'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ${tx.amount} {tx.currency?.toUpperCase()}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(tx.payment_status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {showTransactions && transactions.length === 0 && (
            <div className="p-6 text-center text-gray-500 border-t border-gray-100">
              No payment history yet.
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default Billing;
